import { cloneDeep } from 'lodash';
import { SpecialKeys } from '../shared/utils';
import { VaryCloveStore } from './VaryCloveStore';
import { savePuzzleProgress } from '../../../utils/persistenceUtils';
import { PuzzleNames } from '../../../utils/types';
import { VaryCloveData } from '../../../utils/types';

export const onSolve = () => {
  const { time, puzzleId } = VaryCloveStore.getRawState();

  setTimeout(() => {
    VaryCloveStore.update(s => {
      s.statsDialogIsOpen = true;
    });
  }, 1000);

  savePuzzleProgress(PuzzleNames.VaryClove, {
    puzzleId,
    revealed: false,
    solved: true,
    time,
  });
};

export const onReveal = () => {
  VaryCloveStore.update(s => {
    clearInterval(s.timerUpdatingFn);
    s.revealed = true;
    s.isPaused = false;

    savePuzzleProgress(PuzzleNames.VaryClove, {
      puzzleId: s.puzzleId,
      revealed: true,
      solved: false,
      time: s.time,
    });
  });
};

export const saveVaryClove = () => {
  const { solved, revealed, time, puzzleId } = VaryCloveStore.getRawState();

  if (solved || revealed) return;

  // just updating progress on pause or navigate away
  savePuzzleProgress(PuzzleNames.VaryClove, {
    puzzleId,
    revealed,
    solved,
    time,
  });
};

export const initVaryClove = (puzzleData: VaryCloveData) => {
  VaryCloveStore.update(s => {
    s.puzzleId = puzzleData.puzzleId;
    s.cluePhrase = puzzleData.cluePhrase;
    s.prompt = puzzleData.prompt;
    s.numAlteredLetters = puzzleData.numAlteredLetters;
    s.solution = puzzleData.solution.toLowerCase();
    s.guessWords = [];
    s.solution.split(' ').forEach(word => {
      // e.g. 'fast as hell' => [['', '', '', '',], ['', '',], ['', '', '', '',]]
      const emptyStringArray: string[] = Array(word.length).fill('');
      s.guessWords.push(emptyStringArray);

      const wordCharArray: string[] = word.toLowerCase().split('');
      s.solutionAsCharArrays.push(wordCharArray);
    });
  });
};

// This puzzle's code has a little bit of fuckery because of the challenge of having
// space-separated input sequences where you can type across them continuously.

const moveCursorForward = (arr: any[], cursorLocation: number[]) => {
  const [wordIndex, letterIndex] = cursorLocation;

  if (letterIndex < arr[wordIndex].length - 1) {
    return [wordIndex, letterIndex + 1];
  } else if (wordIndex < arr.length - 1) {
    return [wordIndex + 1, 0];
  } else {
    return cursorLocation;
  }
};

const moveCursorBackward = (arr: any[], cursorLocation: number[]) => {
  const [wordIndex, letterIndex] = cursorLocation;

  if (letterIndex > 0) {
    return [wordIndex, letterIndex - 1];
  } else if (wordIndex > 0) {
    return [wordIndex - 1, arr[wordIndex - 1].length - 1];
  } else {
    return cursorLocation;
  }
};

const updateGuess = (inputLetter: string) => {
  VaryCloveStore.update(s => {
    if (s.solved || s.revealed) return;

    const newGuessWords = cloneDeep(s.guessWords);
    // the letter that WAS in the active InputSquare before keystroke
    const previousLetterAtThisSquare = newGuessWords[s.cursorLocation[0]][s.cursorLocation[1]];

    // If we backspace on an already blank square, move cursor back a step first
    // so that we're clearing the square to the left instead.
    // Otherwise, put letter in current square and move cursor forward.
    if (inputLetter === '' && previousLetterAtThisSquare === '') {
      s.cursorLocation = moveCursorBackward(newGuessWords, s.cursorLocation);
      newGuessWords[s.cursorLocation[0]][s.cursorLocation[1]] = inputLetter;
    } else {
      newGuessWords[s.cursorLocation[0]][s.cursorLocation[1]] = inputLetter;
      s.cursorLocation = moveCursorForward(newGuessWords, s.cursorLocation);
    }

    s.guessWords = newGuessWords;

    const guessWordsCombined = newGuessWords
      .map(word => word.join(''))
      .join(' ');

    if (guessWordsCombined === s.solution) {
      s.solved = true;
      onSolve();
    }
  });
};

export const handleInput = (key: string) => {
  const { solved } = VaryCloveStore.getRawState();
  if (solved) return;

  if (/^[a-zA-Z]$/.test(key)) {
    key = key.toLowerCase();
    updateGuess(key);
  } else if (key === SpecialKeys.Delete) {
    updateGuess('');
  }
};